import React, {
  useRef, useState, useEffect, cloneElement
} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { Icon, Hint } from '../KeyComponents';

import DropdownLabel from './DropdownLabel';
import DropdownHint from './DropdownHint';
import DropdownItem from './DropdownItem';

function Dropdown(props) {
  const {
    size,
    children,
    placeholder,
    state,
    type,
    selected,
    onClick,
    toggle,
    menuPosition,
    className,
    readOnly
  } = props;

  const [inputValue, setInputValue] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedItem, setSelectedItem] = useState(selected || null);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (selected !== undefined && selected !== null) {
      const index = React.Children.toArray(children).findIndex(
        (child) => child.props.value === selected
      );
      if (index !== -1) {
        const item = React.Children.toArray(children)[index].props;
        setSelectedItem(item);
        setSelectedIndex(index);
        setInputValue(item.label);
      }
    } else {
      setSelectedItem(null);
      setSelectedIndex(null);
      setInputValue('');
    }
  }, [selected, children]);

  const handleItemClick = (itemData, index) => {
    setSelectedIndex(index);
    setSelectedItem(itemData);
    setInputValue(itemData.label);
    if (onClick) {
      onClick(itemData); // Keeping this unchanged
    }
    setIsOpen(false);
  };

  const DropdownLabelComp = React.Children.map(children, (child) => {
    if (child.type === DropdownLabel) {
      return cloneElement(child, { state });
    }
    return null;
  });

  const DropdownHintComp = React.Children.map(children, (child) => {
    if (child.type === DropdownHint) {
      return cloneElement(child, { state });
    }
    return null;
  });

  const DropdownItemComp = React.Children.map(children, (child, index) => {
    if (child.type === DropdownItem) {
      const { label, sublabel, ...otherProps } = child.props;

      const searchString = `${label} ${sublabel}`.toLowerCase();
      if (searchString.includes(inputValue.toLowerCase())) {
        return cloneElement(child, {
          onClick: () => handleItemClick({ label, sublabel, ...otherProps }, index),
          selected: index === selectedIndex,
        });
      }
      return null;
    }
    return null;
  });

  const handleIconClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const toggleDropdown = () => {
    if (state !== 'disabled') {
      setIsOpen(!isOpen);
      setInputValue('');
    }
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={classNames('pgn__dropdown', className)} ref={dropdownRef}>
      {DropdownLabelComp}
      {!toggle ? <div
        className={classNames('dropdown-body', `dropdown-${size}`, `dropdown-${state}`,)}
        onClick={toggleDropdown}
        onKeyDown={(e) => e.key === 'Enter' && toggleDropdown()}
        role="button"
        tabIndex={0} // Makes the div focusable and keyboard-accessible
        data-testid="dropdown-body"
      >
        <input
          type="text"
          className="dropdown-input"
          placeholder={selectedItem ? `${selectedItem.label}` : placeholder}
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          disabled={state === 'disabled'}
          readOnly={readOnly}
        />
        {type === 'inline' && (
          <Icon
            className={classNames('dropdown-input-icon', { 'dropdown-toggle': isOpen })}
            icon="arrow-down-s"
            type="line"
            onClick={handleIconClick}
          />
        )}
      </div>
        :
        // add onClick prop to the {toggle}
        React.cloneElement(toggle, { onClick: toggleDropdown })
      }
      <div role="listbox" className={classNames('dropdown-drawer', `dropdown-drawer-${menuPosition}`, { 'dropdown-toggle': isOpen }, { 'extra-toggle': toggle })}>
        {DropdownItemComp && DropdownItemComp.length === 0 ? (
          <Hint icon="">No items</Hint>
        ) : (
          DropdownItemComp
        )}
      </div>
      {DropdownHintComp}
    </div>
  );
}

Dropdown.propTypes = {
  size: PropTypes.string,
  children: PropTypes.node,
  placeholder: PropTypes.string,
  state: PropTypes.oneOf(['default', 'filled', 'error', 'disabled']),
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Can be string or number
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['inline']),
  className: PropTypes.string,
  readOnly: PropTypes.bool,
};

Dropdown.defaultProps = {
  size: 'md',
  placeholder: 'Select an option',
  state: 'default',
  selected: null,
  onClick: null,
  type: '',
  children: null,
  className: '',
  readOnly: false,
};

Dropdown.Label = DropdownLabel;
Dropdown.Hint = DropdownHint;
Dropdown.Item = DropdownItem;

export default Dropdown;
